// Auth context for the whole app. Wraps Supabase auth into a React context
// so screens can read { user, loading } and call { signIn, signUp, signOut,
// resetPassword } without importing the supabase client directly.
//
// Session persistence is handled by Supabase itself (localStorage under the
// hood), so a refresh keeps the user signed in until the JWT expires.

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { setCurrentUser as setStorageUser, migrateBrowserData } from '../lib/userStorage';
import { sessionPreflight, userFromPersistedJwt } from './sessionPreflight';

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>.');
  }
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Role from public.users. 'client' for paying customers (their own
  // data only), 'admin' for the SaaS operator (sees every tenant).
  // Stays null until the role query finishes; consumers should treat
  // null as "loading, don't grant any access yet."
  const [role, setRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(false);
  // Subscription / org snapshot. Driven by the client-journey work
  // (post-payment provisioning): the Stripe webhook stamps the
  // matching subscriptions + organizations rows, AuthContext reads
  // them on sign-in, and the chrome surfaces plan + status pills +
  // past-due banner. null until the queries resolve; consumers should
  // treat null as "loading."
  //
  // Shape:
  //   subscription: { status, plan, current_period_end } | null
  //   organization: { name, plan, status, grace_period_until,
  //                   seat_limit_agents, seat_limit_vas } | null
  const [subscription, setSubscription] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  // Recovery mode. Flipped to true when Supabase fires the
  // PASSWORD_RECOVERY event after the customer lands on the app via
  // a recovery-link email. The render gate in CommissionTracker
  // watches this and shows SetNewPasswordScreen instead of the
  // dashboard until they finish setting a new password (which calls
  // changePassword(), which clears this flag).
  const [recoveryMode, setRecoveryMode] = useState(false);

  useEffect(() => {
    let mounted = true;

    // ── Order matters ─────────────────────────────────────────────
    // Supabase processes recovery URLs asynchronously after createClient
    // (detectSessionInUrl: true). When the user lands via a recovery
    // link, Supabase exchanges the token for a session AND fires
    // PASSWORD_RECOVERY. If we call getSession() before attaching the
    // listener, that event can fire on the same tick before our
    // handler exists → we miss it → user lands in the dashboard
    // holding their old (forgotten) password.
    //
    // Per Supabase docs: ALWAYS attach onAuthStateChange first.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
// BUG-004: Only log meaningful auth transitions (not token refreshes)
              // eslint-disable-next-line no-console
              if (event !== 'SIGNED_IN' || !session) console.log('[auth] event=', event, 'hasSession=', !!session);
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryMode(true);
      } else if (event === 'SIGNED_OUT') {
        // Belt and suspenders: clear recovery mode on sign-out so a
        // re-login on the same browser doesn't inherit the flag.
        setRecoveryMode(false);
      }
      setUser(session?.user ?? null);
    });

    // ── Fallback URL detection ────────────────────────────────────
    // If for any reason the PASSWORD_RECOVERY event doesn't fire (older
    // Supabase versions, race conditions, hash-vs-query format quirks,
    // …), parse the URL on mount and flip recoveryMode manually. This
    // is idempotent — the event handler does the same thing.
    if (typeof window !== 'undefined') {
      const hash = window.location.hash || '';
      const search = window.location.search || '';
      const params = new URLSearchParams(
        hash.startsWith('#') ? hash.slice(1) : search.startsWith('?') ? search.slice(1) : ''
      );
      // Recovery links from Supabase carry `type=recovery`. The
      // exchange itself is handled by detectSessionInUrl; we just
      // surface the intent to React state so the gate fires.
      if (params.get('type') === 'recovery') {
        setRecoveryMode(true);
      }
      // Surface OAuth/recovery errors that Supabase puts in the hash
      // when the link is expired or already used. Console-warn for
      // now — a future iteration can wire this into a banner.
      const err = params.get('error');
      const errCode = params.get('error_code');
      const errDesc = params.get('error_description');
      if (err || errCode) {
        // eslint-disable-next-line no-console
        console.warn('[auth] URL error on mount:', { err, errCode, errDesc });
      }
    }

    // ── Preflight: verify persisted JWT is fresh BEFORE any Supabase call ──
    // Runs synchronously against localStorage — decodes the JWT's `exp`
    // claim and purges the stored session if it's expired or within
    // 60 seconds of expiring. This is the ROOT-CAUSE fix for the
    // "Loading..." hang: the Supabase client only enters the infinite
    // refresh loop when handed a stale token. If we never hand it one,
    // the loop can't start. The watchdog + auto-purge below remain as
    // defense-in-depth for edge cases the preflight can't catch (like
    // a fresh JWT paired with an unreachable Supabase edge).
    const preflight = sessionPreflight();
    if (!preflight.ok) {
      // eslint-disable-next-line no-console
      console.info('[auth] preflight purged stale session:', preflight.reason);
    }

    // ── Now hydrate any existing persisted session ───────────────
    // A bad/expired JWT in localStorage used to hang this Promise
    // forever, leaving the whole app stuck on "Loading…". Belt: 8s
    // watchdog fires so the user isn't stuck on a spinner. Suspenders:
    // .catch() handles a straight reject.
    //
    // Preflight-aware watchdog — the old behavior of nuking the
    // session tokens on watchdog fire was too aggressive: on a slow
    // network / cold Supabase edge, getSession() with a demonstrably
    // fresh token can take >5s to resolve. When that happened the
    // watchdog would log the user out mid-reload.
    //
    // The fix: if sessionPreflight already validated the JWT as fresh,
    // build the user object from the JWT payload directly and boot the
    // app. Supabase's background refresh will catch up on the next
    // Auth call. Only purge when we have NO fresh JWT to trust — that
    // means preflight said `!ok` OR userFromPersistedJwt returned null.
    // Bumped from 5s → 8s so the fallback path fires less often.
    const watchdog = setTimeout(() => {
      if (!mounted) return;
      const jwtUser = preflight.ok ? userFromPersistedJwt() : null;
      if (jwtUser) {
        // eslint-disable-next-line no-console
        console.warn('[auth] getSession watchdog fired — booting with fresh persisted JWT (Supabase slow, session valid)');
        setUser(jwtUser);
        setLoading(false);
        return;
      }
      // eslint-disable-next-line no-console
      console.warn('[auth] getSession watchdog fired — no fresh JWT, force-clearing loading + purging bad session');
      // A hung getSession with no fresh JWT means the token in
      // localStorage is bad but still parses (Supabase stuck in a
      // refresh loop). Nuke the local session tokens directly so the
      // login screen is a guaranteed clean slate.
      try {
        for (let i = window.localStorage.length - 1; i >= 0; i--) {
          const k = window.localStorage.key(i);
          if (k && (k.startsWith('sb-') || k.includes('supabase.auth.'))) {
            window.localStorage.removeItem(k);
          }
        }
      } catch { /* ignore storage errors */ }
      setUser(null);
      setLoading(false);
    }, 8000);
    supabase.auth.getSession()
      .then(({ data }) => {
        if (!mounted) return;
        setUser(data?.session?.user ?? null);
        setLoading(false);
        clearTimeout(watchdog);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[auth] getSession failed — dropping to login:', err?.message || err);
        if (!mounted) return;
        setUser(null);
        setLoading(false);
        clearTimeout(watchdog);
      });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  // Per-user storage wiring. Runs whenever user identity changes so the
  // userStorage module's namespace tracks Supabase's session, and the
  // one-time browser-data migration claims any pre-existing un-prefixed
  // app keys for the first user to sign in on this device.
  useEffect(() => {
    if (user?.id) {
      setStorageUser(user.id);
      migrateBrowserData(user.id);
    } else {
      setStorageUser(null);
    }
  }, [user?.id]);

  // Load this user's role from public.users (migration 004). Re-runs
  // on every identity change so a sign-out clears the role and a new
  // sign-in fetches fresh. Fails closed: any error → role stays null
  // → the app treats them as "no access" until the next query succeeds.
  //
  // Why a dedicated table instead of reading from auth metadata: roles
  // belong with the rest of the app's RLS-scoped data so admin promotion
  // is auditable and revocable via SQL, not buried in auth user_metadata
  // which is writable from the client.
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setRole(null);
      setRoleLoading(false);
      return;
    }
    setRoleLoading(true);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();
        if (cancelled) return;
        if (error) {
          // Common cause in the wild: migration 004 hasn't been
          // applied yet (`relation "public.users" does not exist`).
          // Fall back to 'client' so paying customers still get into
          // their CRM, and warn loudly so we know to apply the
          // migration.
          // eslint-disable-next-line no-console
          console.warn('[auth] role query failed — defaulting to client. Apply supabase_migrations/004_users_roles.sql to enable admin role.', error.message);
          setRole('client');
        } else {
          setRole(data?.role || 'client');
        }
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[auth] role query threw — defaulting to client.', err);
        setRole('client');
      } finally {
        if (!cancelled) setRoleLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Load subscription + organization snapshots in parallel on every
  // identity change. Both are read-only here — the Stripe webhook
  // (api/webhooks/stripe.js) is the source of truth for writes.
  // Failures degrade gracefully: the chrome just won't show plan /
  // status pills, the customer journey isn't blocked.
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setSubscription(null);
      setOrganization(null);
      setAccountLoading(false);
      return;
    }
    setAccountLoading(true);
    (async () => {
      try {
        const [subRes, orgRes] = await Promise.all([
          supabase
            .from('subscriptions')
            .select('status, plan, current_period_end')
            .eq('user_id', user.id)
            .maybeSingle(),
          supabase
            .from('organizations')
            .select('name, plan, status, grace_period_until, seat_limit_agents, seat_limit_vas')
            .eq('owner_user_id', user.id)
            .maybeSingle(),
        ]);
        if (cancelled) return;
        if (subRes.error) {
          // Most common: migration 003 hasn't been applied yet.
          // Don't block the user — just leave subscription null.
          // eslint-disable-next-line no-console
          console.warn('[auth] subscription query failed:', subRes.error.message);
          setSubscription(null);
        } else {
          setSubscription(subRes.data || null);
        }
        if (orgRes.error) {
          // eslint-disable-next-line no-console
          console.warn('[auth] organization query failed:', orgRes.error.message);
          setOrganization(null);
        } else {
          setOrganization(orgRes.data || null);
        }
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[auth] account query threw:', err);
      } finally {
        if (!cancelled) setAccountLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { ok: false, error: error.message };
    // Audit 2026-07-01: enrollment card ships without a matching
    // login-side challenge, so a user who enrolls in TOTP would get
    // stuck at aal1 (password-only) and never satisfy any RLS
    // policy that eventually gates on aal2. Detect that state here
    // and hand it back to the caller as { mfaRequired, factorId }
    // so the login screen can render the code prompt.
    try {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      const needsChallenge = aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2';
      if (needsChallenge) {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const list = factors?.totp || factors?.all || [];
        const verified = list.find(f => f.status === 'verified' && (f.factor_type === 'totp' || !f.factor_type));
        if (verified) {
          return { ok: false, mfaRequired: true, factorId: verified.id, user: data.user };
        }
      }
    } catch { /* fall through — mfa API unavailable → treat as normal signin */ }
    return { ok: true, user: data.user };
  }, []);

  // Complete the TOTP challenge after a successful signInWithPassword
  // that returned mfaRequired. Returns { ok } or { ok:false, error }.
  const verifyMfa = useCallback(async ({ factorId, code }) => {
    if (!factorId) return { ok: false, error: 'missing_factor_id' };
    const clean = String(code || '').replace(/\D/g, '');
    if (clean.length !== 6) return { ok: false, error: 'Enter the 6-digit code from your authenticator app.' };
    try {
      const chal = await supabase.auth.mfa.challenge({ factorId });
      if (chal.error) return { ok: false, error: chal.error.message };
      const ver = await supabase.auth.mfa.verify({
        factorId,
        challengeId: chal.data.id,
        code: clean,
      });
      if (ver.error) return { ok: false, error: ver.error.message };
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || String(e) };
    }
  }, []);

  const signUp = useCallback(async (email, password, metadata) => {
    // metadata (business_name, business_address, signup_phone) is
    // stamped onto the Supabase auth user via options.data so it lands
    // in user_metadata regardless of whether the Stripe webhook creates
    // the subscriptions row before or after this signup completes. The
    // webhook backfills the subscriptions row from this metadata (see
    // api/webhooks/stripe.js computeBusinessInfoBackfill) so ASCEND's
    // autonomous number provisioning never stalls waiting on Settings.
    const options = (metadata && Object.keys(metadata).length > 0)
      ? { data: metadata }
      : undefined;
    const { data, error } = await supabase.auth.signUp({ email, password, options });
    if (error) return { ok: false, error: error.message };
    return { ok: true, user: data.user };
  }, []);

  const signOut = useCallback(async () => {
    // Audit 2026-06-29 H2: detach the userStorage namespace FIRST
    // so any persist-on-change effects that fire during the sign-
    // out sequence write to the anonymous slot instead of clobbering
    // the outgoing user's cloud-backed data (this exact bug bit us
    // once already — see CommissionTracker.jsx:2751 for the mirror).
    setStorageUser(null);
    const { error } = await supabase.auth.signOut();
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  }, []);

  const resetPassword = useCallback(async (email) => {
    // Redirect the user back to the app after they click the email link.
    // Supabase's detectSessionInUrl option will pick up the recovery token.
    const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/` : undefined;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  }, []);

  // Two callers:
  //   1. SetNewPasswordScreen after the customer clicks the recovery
  //      link in their email. recoveryMode is true; the screen calls
  //      exitRecoveryMode() AFTER showing the success state so the
  //      gate doesn't release mid-animation.
  //   2. The Settings → Change Password panel for users who are
  //      already signed in normally and just want to rotate. recoveryMode
  //      isn't true here so the screen just toasts and moves on.
  // Supabase's updateUser({ password }) requires a current session,
  // which both paths have.
  const changePassword = useCallback(async (newPassword) => {
    if (!newPassword) return { ok: false, error: 'Password is required.' };
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  }, []);

  // Explicit gate-release. SetNewPasswordScreen calls this after the
  // success message has been visible for a moment so the user actually
  // sees "✓ Password updated" before being thrown back into the CRM.
  const exitRecoveryMode = useCallback(() => {
    setRecoveryMode(false);
  }, []);

  const value = {
    user, loading,
    role, roleLoading,
    subscription, organization, accountLoading,
    recoveryMode, exitRecoveryMode,
    signIn, signUp, signOut, resetPassword, changePassword, verifyMfa,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
